import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { getVitals, getHealthScore, getMedications, getHealthProfile } from '../services/api';

interface Vital {
  date: string;
  weight_kg?: number;
  heart_rate_bpm?: number;
  systolic_bp?: number;
  diastolic_bp?: number;
  blood_oxygen_pct?: number;
  sleep_hours?: number;
  steps?: number;
  hrv_ms?: number;
  temperature_c?: number;
  glucose_mmol?: number;
  notes?: string;
}

interface HealthFlag {
  severity: string;
  metric: string;
  value: string;
  message: string;
}

const SEVERITY_SYMBOL: Record<string, string> = {
  critical: '[CRITICAL]',
  high: '[HIGH]',
  medium: '[MEDIUM]',
  low: '[INFO]',
};

function fmt(val: number | undefined, unit: string, decimals = 1) {
  return val != null ? `${Number(val).toFixed(decimals)} ${unit}` : '—';
}

export default function ReportPage() {
  const { currentUser, userProfile } = useAuth();
  const [vitals, setVitals] = useState<Vital[]>([]);
  const [score, setScore] = useState<any>(null);
  const [meds, setMeds] = useState<any[]>([]);
  const [profile, setProfile] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const reportDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  const reportRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const [vitalsRes, scoreRes, medsRes, profileRes] = await Promise.allSettled([
        getVitals(token, 30),
        getHealthScore(token),
        getMedications(token),
        getHealthProfile(token),
      ]);
      if (vitalsRes.status === 'fulfilled') setVitals((vitalsRes.value.vitals || []).slice().reverse());
      if (scoreRes.status === 'fulfilled') setScore(scoreRes.value);
      if (medsRes.status === 'fulfilled') setMeds(medsRes.value.medications || []);
      if (profileRes.status === 'fulfilled') setProfile(profileRes.value.profile || {});
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => { load(); }, [load]);

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mr-3" />
        Generating report...
      </div>
    );
  }

  const displayName = userProfile?.display_name || currentUser?.email || 'User';

  return (
    <>
      {/* Print button — hidden when printing */}
      <div className="print:hidden mb-6 flex items-center justify-between max-w-3xl">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Health Report</h1>
          <p className="text-gray-500 text-sm mt-1">30-day summary • {reportDate}</p>
        </div>
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
        >
          <span>⬇</span> Download PDF
        </button>
      </div>

      {/* Report content */}
      <div
        ref={reportRef}
        className="max-w-3xl bg-white rounded-2xl border border-gray-200 p-8 space-y-8 print:rounded-none print:border-none print:shadow-none print:p-0 print:max-w-none"
        style={{ fontFamily: 'Georgia, serif' }}
      >
        {/* Header */}
        <div className="border-b border-gray-200 pb-6 print:border-gray-400">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-2xl font-bold text-emerald-700 print:text-black">♥ VitalQuant</p>
              <p className="text-sm text-gray-500 mt-0.5">Personal Health Report</p>
            </div>
            <div className="text-right text-sm text-gray-500">
              <p className="font-semibold text-gray-800">{displayName}</p>
              <p>{reportDate}</p>
              <p>30-day summary</p>
            </div>
          </div>

          {/* Patient details if in profile */}
          {(profile.age || profile.sex || profile.height_cm || profile.blood_type) && (
            <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600">
              {profile.age && <span>Age: <strong>{profile.age}</strong></span>}
              {profile.sex && <span>Sex: <strong>{profile.sex}</strong></span>}
              {profile.height_cm && <span>Height: <strong>{profile.height_cm} cm</strong></span>}
              {profile.blood_type && <span>Blood type: <strong>{profile.blood_type}</strong></span>}
            </div>
          )}
        </div>

        {/* Health Score */}
        {score && score.score !== null && (
          <section>
            <h2 className="text-lg font-bold text-gray-800 mb-3 border-b border-gray-100 pb-1">Health Score</h2>
            <div className="flex items-center gap-6">
              <div className="text-5xl font-extrabold text-emerald-700 print:text-black">
                {score.score}<span className="text-2xl text-gray-400">/100</span>
              </div>
              <div>
                <p className="text-xl font-bold text-gray-800">{score.grade}</p>
                {score.based_on_date && (
                  <p className="text-sm text-gray-400">Based on vitals from {score.based_on_date}</p>
                )}
              </div>
            </div>

            {score.flags && score.flags.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-semibold text-gray-700">Risk Flags:</p>
                {score.flags.map((flag: HealthFlag, i: number) => (
                  <div key={i} className="flex gap-3 text-sm pl-2 border-l-2 border-gray-300">
                    <span className="font-mono text-xs text-gray-500 mt-0.5 flex-shrink-0">
                      {SEVERITY_SYMBOL[flag.severity] || flag.severity.toUpperCase()}
                    </span>
                    <span>
                      <strong>{flag.metric}</strong> — {flag.value}: {flag.message}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Vitals Table */}
        {vitals.length > 0 && (
          <section>
            <h2 className="text-lg font-bold text-gray-800 mb-3 border-b border-gray-100 pb-1">
              Vitals Log (last 30 days)
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b-2 border-gray-300">
                    <th className="text-left py-2 pr-3 font-semibold text-gray-700">Date</th>
                    <th className="text-right py-2 px-2 font-semibold text-gray-700">Weight</th>
                    <th className="text-right py-2 px-2 font-semibold text-gray-700">HR</th>
                    <th className="text-right py-2 px-2 font-semibold text-gray-700">BP</th>
                    <th className="text-right py-2 px-2 font-semibold text-gray-700">SpO₂</th>
                    <th className="text-right py-2 px-2 font-semibold text-gray-700">Sleep</th>
                    <th className="text-right py-2 px-2 font-semibold text-gray-700">Steps</th>
                    <th className="text-right py-2 px-2 font-semibold text-gray-700">HRV</th>
                    <th className="text-right py-2 pl-2 font-semibold text-gray-700">Glucose</th>
                  </tr>
                </thead>
                <tbody>
                  {vitals.map((v, i) => (
                    <tr key={v.date} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="py-1.5 pr-3 text-gray-700 font-medium">{v.date}</td>
                      <td className="py-1.5 px-2 text-right text-gray-600">{v.weight_kg ? `${v.weight_kg} kg` : '—'}</td>
                      <td className="py-1.5 px-2 text-right text-gray-600">{v.heart_rate_bpm ? `${v.heart_rate_bpm}` : '—'}</td>
                      <td className="py-1.5 px-2 text-right text-gray-600">
                        {v.systolic_bp && v.diastolic_bp ? `${v.systolic_bp}/${v.diastolic_bp}` : '—'}
                      </td>
                      <td className="py-1.5 px-2 text-right text-gray-600">{v.blood_oxygen_pct ? `${v.blood_oxygen_pct}%` : '—'}</td>
                      <td className="py-1.5 px-2 text-right text-gray-600">{v.sleep_hours ? `${v.sleep_hours}h` : '—'}</td>
                      <td className="py-1.5 px-2 text-right text-gray-600">{v.steps ? v.steps.toLocaleString() : '—'}</td>
                      <td className="py-1.5 px-2 text-right text-gray-600">{v.hrv_ms ? `${v.hrv_ms}` : '—'}</td>
                      <td className="py-1.5 pl-2 text-right text-gray-600">{v.glucose_mmol ? `${v.glucose_mmol}` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Averages summary */}
        {vitals.length > 0 && (
          <section>
            <h2 className="text-lg font-bold text-gray-800 mb-3 border-b border-gray-100 pb-1">30-Day Averages</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { label: 'Weight', vals: vitals.filter(v => v.weight_kg).map(v => v.weight_kg!), unit: 'kg' },
                { label: 'Heart Rate', vals: vitals.filter(v => v.heart_rate_bpm).map(v => v.heart_rate_bpm!), unit: 'bpm' },
                { label: 'Systolic BP', vals: vitals.filter(v => v.systolic_bp).map(v => v.systolic_bp!), unit: 'mmHg' },
                { label: 'Sleep', vals: vitals.filter(v => v.sleep_hours).map(v => v.sleep_hours!), unit: 'h' },
                { label: 'Steps', vals: vitals.filter(v => v.steps).map(v => v.steps!), unit: 'steps', round: true },
                { label: 'Blood Oxygen', vals: vitals.filter(v => v.blood_oxygen_pct).map(v => v.blood_oxygen_pct!), unit: '%' },
              ].map(({ label, vals, unit, round }) => {
                if (vals.length === 0) return null;
                const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
                return (
                  <div key={label} className="border border-gray-100 rounded-lg p-3">
                    <p className="text-xs text-gray-500">{label}</p>
                    <p className="text-base font-bold text-gray-800 mt-0.5">
                      {round ? Math.round(avg).toLocaleString() : avg.toFixed(1)} <span className="text-xs font-normal text-gray-400">{unit}</span>
                    </p>
                    <p className="text-xs text-gray-400">{vals.length} readings</p>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Medications */}
        {meds.length > 0 && (
          <section>
            <h2 className="text-lg font-bold text-gray-800 mb-3 border-b border-gray-100 pb-1">Current Medications</h2>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 font-semibold text-gray-700">Medication</th>
                  <th className="text-left py-2 pr-4 font-semibold text-gray-700">Dosage</th>
                  <th className="text-left py-2 pr-4 font-semibold text-gray-700">Frequency</th>
                  <th className="text-left py-2 font-semibold text-gray-700">Notes</th>
                </tr>
              </thead>
              <tbody>
                {meds.map((m) => (
                  <tr key={m.id} className="border-b border-gray-100">
                    <td className="py-2 pr-4 font-medium text-gray-800">{m.name}</td>
                    <td className="py-2 pr-4 text-gray-600">{m.dosage || '—'}</td>
                    <td className="py-2 pr-4 text-gray-600">{m.frequency?.replace(/_/g, ' ') || '—'}</td>
                    <td className="py-2 text-gray-500 text-xs">{m.notes || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Medical conditions from profile */}
        {(profile.conditions?.length > 0 || profile.family_history?.length > 0) && (
          <section>
            <h2 className="text-lg font-bold text-gray-800 mb-3 border-b border-gray-100 pb-1">Medical History</h2>
            {profile.conditions?.length > 0 && (
              <div className="mb-2">
                <p className="text-sm font-semibold text-gray-700 mb-1">Conditions:</p>
                <p className="text-sm text-gray-600">{profile.conditions.join(', ')}</p>
              </div>
            )}
            {profile.family_history?.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1">Family History:</p>
                <p className="text-sm text-gray-600">{profile.family_history.join(', ')}</p>
              </div>
            )}
          </section>
        )}

        {/* Footer */}
        <div className="border-t border-gray-200 pt-4 text-xs text-gray-400 print:border-gray-300">
          <p>Generated by VitalQuant on {reportDate}. This report is for personal reference only and does not constitute medical advice. Always consult a qualified healthcare professional for medical decisions.</p>
        </div>
      </div>
    </>
  );
}
